import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  CheckBox,
  CheckBoxDomRef,
  Icon,
  List,
  ListItemStandard,
  Table,
  TableCell,
  TableGrowing,
  TableHeaderCell,
  TableHeaderRow,
  TableDomRef,
  TableRow,
  Text,
  Title,
  Toast,
  Toolbar,
  ToolbarSeparator,
  ToolbarSpacer,
  Ui5CustomEvent
} from '@ui5/webcomponents-react';
import { useStyles } from './ManageQueries.jss';
import { Link, useHistory } from 'react-router-dom';
import {
  BaseQuery,
  BaseQueryFilters,
  GetQueriesPageQuery,
  Maybe,
  QueryDetails,
  ResourceAccessLevel,
  Status,
  useGetQueriesPageQuery,
  useGetUserInfoQuery
} from 'generated/graphql';
import { useTranslation } from 'react-i18next';
import { Filters } from './Filters';
import { env } from 'config/env';
import '@ui5/webcomponents-icons/dist/navigation-right-arrow';
import '@ui5/webcomponents-icons/dist/upload';
import '@ui5/webcomponents-icons/dist/action-settings';
import '@ui5/webcomponents-icons/dist/add';
import '@ui5/webcomponents-icons/dist/excel-attachment';
import { SearchColumnsDialog } from './SearchColumnsDialog';
import { useSearchColumnsStore } from 'stores/SearchColumnsStore';
import { SearchColumnsVisibility } from 'stores/interfaces/SearchColumnsState';
import { AssignDialog } from './AssignDialog/AssignDialog';
import { BatchAssignMessageBox } from './BatchAssignMessageBox/BatchAssignMessageBox';
import { ResultUserMessage, UserMessage } from 'components/shared/UserMessage';
import { usePrevious } from 'hooks/usePrevious';
import { useGetDate } from 'hooks/useGetDate';
import isEqual from 'lodash/isEqual';
import { ImportDialog } from './ImportDialog/ImportDialog';
import {
  MKTQueryProps,
  MKT_FILENAME_PROP,
  QT_IMPORT_AZURE_ROLE_VALUE,
  XTMQueryProps,
  XTM_FILENAME_PROP,
  XTM_PROJECTNAME_PROP
} from '../../constants';
import { FeatureGuard } from 'auth/FeatureGuard';
import {
  getProjectManager,
  getQualityExpert,
  getQueryResponsible,
  getSubstituteProjectManager
} from 'utils/projectStakeholders';
import { formatUserName } from 'utils/formatUserName';
import { useDownloadResultQueries } from 'hooks/useDownloadResultQueries';
import { OverflowMenu } from 'components/shared/OverflowMenu/OverflowMenu';
import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import ListItemType from '@ui5/webcomponents/dist/types/ListItemType.js';
import ListSeparators from '@ui5/webcomponents/dist/types/ListSeparator.js';
import TableGrowingMode from '@ui5/webcomponents/dist/types/TableGrowingMode.js';
import ToolbarDesign from '@ui5/webcomponents/dist/types/ToolbarDesign.js';
interface QueryPageVariables {
  filters: BaseQueryFilters;
  cursor: string | null;
}

export const ManageQueries: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const PAGE_SIZE = Number(env?.REACT_APP_PAGINATION_SIZE) || 50;
  const MAX_DOWNLOAD_SIZE_DEFAULT = '100';
  const TOAST_DURATION = 3000;
  const allowQTImports = env?.REACT_APP_ALLOW_QUERY_TRACKER_IMPORTS === 'true' ? true : false;
  const headerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<TableDomRef>(null);
  const toastRef = useRef<any>();
  const [toastOpen, setToastOpen] = useState(false);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<JSX.Element[]>([]);
  const [searchColumnsDialogOpen, setSearchColumnsDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedQueries, setSelectedQueries] = useState<BaseQuery[]>([]);
  const [selectableQueries, setSelectableQueries] = useState<BaseQuery[]>([]);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState<boolean>(false);
  const [isFetchingSearchFilters, setIsFetchingSearchFilters] = useState<boolean>(true);
  const [dataRefreshed, setDataRefreshed] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState('');

  const [queryPageVariables, setQueryPageVariables] = useState<QueryPageVariables>({
    filters: {},
    cursor: null
  });

  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
  const [filtersChanged, setFiltersChanged] = useState<boolean>(false);
  const previousQueryPageVariables = usePrevious(queryPageVariables);
  const [isVisibleUserMessage, setIsVisibleUserMessage] = useState(false);
  const [resultUserMessage, setResultUserMessage] = useState<ResultUserMessage>({
    isSuccess: false,
    message: ''
  });
  const [batchAssignNonNewQuery, setBatchAssignNonNewQuery] = useState(false);

  const hasChangedColumnVisibility = useSearchColumnsStore(
    (state) => state.hasChangedColumnVisibility
  );
  const setHasChangedColumnVisibility = useSearchColumnsStore(
    (state) => state.setHasChangedColumnVisibility
  );
  const searchColumnsVisibility = useSearchColumnsStore((state) => state.searchColumnsVisibility);
  const setSearchColumnsVisibility = useSearchColumnsStore(
    (state) => state.setSearchColumnsVisibility
  );

  const [{ data: userData, fetching: fetchingUserData }] = useGetUserInfoQuery({
    requestPolicy: 'cache-and-network'
  });

  const [{ fetching, data }, reExecuteQuery] = useGetQueriesPageQuery({
    pause: fetchingUserData || isFetchingSearchFilters || !filtersChanged,
    requestPolicy: 'cache-and-network',
    variables: {
      limit: PAGE_SIZE,
      after: queryPageVariables.cursor,
      filters: queryPageVariables.filters,
      isInternal: userData?.user?.internal
    }
  });
  const maxQueriesResult = parseInt(
    (userData?.user?.internal
      ? env?.REACT_APP_MAX_DOWNLOAD_QUERIES_RESULT_FOR_INTERNAL_USER
      : env?.REACT_APP_MAX_DOWNLOAD_QUERIES_RESULT_FOR_EXTERNAL_USER) ?? MAX_DOWNLOAD_SIZE_DEFAULT,
    10
  );

  const refetchQueriesPage = () => {
    reExecuteQuery();
    setDataRefreshed(true);
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setToastOpen(true);
  };

  const { getFormattedDateString } = useGetDate();
  const { onDownloadResults } = useDownloadResultQueries({
    searchColumnsVisibility,
    showToastMessage
  });

  /**
   * Forces every change on queryPageVariables to call the useGetQueriesPageQuery
   */
  useEffect(() => {
    if (isEqual(previousQueryPageVariables, queryPageVariables)) {
      refetchQueriesPage();
    }
  }, [queryPageVariables]);

  useEffect(() => {
    resetRows();
  }, [
    queryPageVariables.filters.project,
    queryPageVariables.filters.search,
    queryPageVariables.filters.status,
    queryPageVariables.filters.targetLanguage,
    queryPageVariables.filters.createdBy,
    queryPageVariables.filters.createdAt,
    queryPageVariables.filters.qualityExpert,
    queryPageVariables.filters.projectManager,
    queryPageVariables.filters.queryResponsible,
    queryPageVariables.filters.assignee,
    queryPageVariables.filters.translationType,
    queryPageVariables.filters.xtmProjectName,
    queryPageVariables.filters.filename,
    queryPageVariables.filters.submitterGroupName,
    queryPageVariables.filters.loio,
    queryPageVariables.filters.repository,
    queryPageVariables.filters.collection,
    queryPageVariables.filters.stringId,
    queryPageVariables.filters.distributed,
    queryPageVariables.filters.queryTrackerId,
    queryPageVariables.filters.implementationStatus,
    queryPageVariables.filters.implementationLanguage,
    queryPageVariables.filters.applicationComponent,
    queryPageVariables.filters.account,
    queryPageVariables.filters.issueType,
    queryPageVariables.filters.substitutePm
  ]);

  useEffect(() => {
    if (data && data.response.edges && !fetching) {
      const queries = mapQueries(data);
      setRows(() => createRows(queries));
      setSelectableQueries(
        queries.filter((q) => q.status !== Status.COMPLETED && q.status !== Status.CLOSED)
      );
      setDataRefreshed(false);
    }
  }, [data?.response.edges, searchColumnsVisibility, dataRefreshed, selectedIds]);

  const mapQueries = (data: GetQueriesPageQuery) => {
    return data.response.edges?.map((edge) => {
      if (edge.node.queryDetails?.__typename === 'DitaQuery')
        return {
          ...edge.node,
          queryDetails: {
            ...edge.node.queryDetails,
            xtmProjectName: edge.node.queryDetails.xtmProjectNameDita
          }
        };
      if (edge.node.queryDetails?.__typename === 'CorporateContentQuery')
        return {
          ...edge.node,
          queryDetails: {
            ...edge.node.queryDetails,
            xtmProjectName: edge.node.queryDetails.xtmProjectNameCC
          }
        };
      if (edge.node.queryDetails?.__typename === 'MarketingQuery')
        return {
          ...edge.node,
          queryDetails: {
            ...edge.node.queryDetails,
            fileName: edge.node.queryDetails.fileNameMkt
          }
        };
      return edge.node;
    }) as BaseQuery[];
  };

  useEffect(() => {
    if (data && data.response.total !== total) {
      setTotal(data.response.total);
    }
  }, [data?.response.total]);

  useEffect(() => {
    if (data !== undefined) {
      setInitialDataLoaded(true);
    }
  }, [data]);

  useEffect(() => {
    const table = tableRef.current;
    const tableDiv = document.getElementById('manage-queries-table');
    if (!table || !tableDiv) return;
    const setHeight = () => {
      if (tableDiv.clientHeight > 0) {
        table.style.height = `${tableDiv.clientHeight}px`;
      }
    };
    setHeight();
    const ro = new ResizeObserver(setHeight);
    ro.observe(tableDiv);
    return () => ro.disconnect();
  }, []);

  const allSelectableSelected =
    selectableQueries.length > 0 && selectableQueries.every((q) => selectedIds.includes(q.id));
  const isSelectAllIndeterminate = selectedIds.length > 0 && !allSelectableSelected;

  const isInternalUser = () => {
    return userData?.user?.internal ?? true;
  };

  const resetRows = () => {
    setSelectedIds([]);
    setSelectedQueries([]);
    setSelectableQueries([]);
    setRows([]);
  };

  const handleCheckboxClick = (item: BaseQuery, event: Ui5CustomEvent<CheckBoxDomRef>) => {
    const { checked } = event.target;
    if (checked) {
      setSelectedIds((prev) => [...prev, item.id]);
      setSelectedQueries((prev) => [...prev, item]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => id !== item.id));
      setSelectedQueries((prev) => prev.filter((query) => query.id !== item.id));
    }
  };

  const handleSelectAllChange = (event: Ui5CustomEvent<CheckBoxDomRef>) => {
    const { checked } = event.target;
    if (checked) {
      setSelectedIds(selectableQueries.map((q) => q.id));
      setSelectedQueries(selectableQueries);
    } else {
      setSelectedIds([]);
      setSelectedQueries([]);
    }
  };

  const handleTableRowClick = (event: Ui5CustomEvent<TableDomRef, { row: HTMLElement }>) => {
    const rowId = event.detail?.row?.id ?? (event.target as HTMLElement).id;
    history.push(`/queries/${rowId}`);
  };

  const handleTableRowArrowClick = (queryId: string) => {
    history.push(`/queries/${queryId}`);
  };

  const handleFilterChange = (newFilters: BaseQueryFilters, isUserInteraction?: boolean) => {
    if (
      isEqual(newFilters, queryPageVariables.filters) &&
      initialDataLoaded &&
      !isUserInteraction
    ) {
      setFiltersChanged(false);
      return;
    }

    setQueryPageVariables({
      filters: newFilters,
      cursor: null
    });
    setFiltersChanged(true);
    resetRows();
  };

  const handleSearchQueriesFetchChange = (fetching: boolean) => {
    if (isFetchingSearchFilters !== fetching) {
      setIsFetchingSearchFilters(fetching);
    }
  };

  const onLoadMore = () => {
    const isPossibleToLoadMore = () =>
      !hasChangedColumnVisibility &&
      rows.length > 0 &&
      !fetching &&
      data?.response.pageInfo.endCursor &&
      data?.response.pageInfo.hasNextPage;

    if (isPossibleToLoadMore()) {
      setQueryPageVariables((prev) => ({
        filters: prev.filters,
        cursor: data?.response.pageInfo.endCursor || null
      }));
    } else {
      setHasChangedColumnVisibility(false);
    }
  };

  const onConfirmColumnVisibility = (visibility: SearchColumnsVisibility) => {
    setHasChangedColumnVisibility(true);
    setSearchColumnsVisibility(visibility);
    refetchQueriesPage();
  };

  const onResultUserMessage = (resultMessage: ResultUserMessage) => {
    setResultUserMessage(resultMessage);
    setIsVisibleUserMessage(true);
  };

  const onResetUserMessage = () => {
    setResultUserMessage({ isSuccess: false, message: '' });
    setIsVisibleUserMessage(false);
  };

  const renderTableFooter = () => {
    if (rows.length > 0 && !data?.response.pageInfo.hasNextPage) {
      return null;
    }
    if (data && data.response.total > 0) {
      return (
        <Button className={classes.fetchItemsButton} onClick={onLoadMore} disabled={fetching}>
          {t('manage-queries-fetch-new-items')}
        </Button>
      );
    }
    return null;
  };

  const getXTMQueryDetailsValue = (
    queryDetails: Maybe<QueryDetails>,
    xtmProp: XTMQueryProps
  ): string => {
    if (queryDetails) {
      if (
        queryDetails.__typename === 'TrainingQuery' ||
        queryDetails.__typename === 'DitaQuery' ||
        queryDetails.__typename === 'NonAbapQuery' ||
        queryDetails.__typename === 'CorporateContentQuery'
      ) {
        return queryDetails[xtmProp] || '';
      }
    }
    return '';
  };

  const getMktQueryDetailsValue = (queryDetails: QueryDetails, mktProp: MKTQueryProps): string => {
    if (queryDetails.__typename === 'MarketingQuery') {
      return queryDetails[mktProp] || '';
    }
    return '';
  };

  const getApplicationComponent = (row: BaseQuery) => {
    const isNotesQuery = row?.queryDetails?.__typename === 'NotesQuery';
    const isAbapQuery = row?.queryDetails?.__typename === 'AbapQuery';

    if (isNotesQuery) {
      return row?.queryDetails?.__typename === 'NotesQuery' ? row.queryDetails?.component : '';
    }

    if (isAbapQuery) {
      return row?.queryDetails?.__typename === 'AbapQuery'
        ? row.queryDetails?.applicationComponent
        : '';
    }

    return '';
  };

  const getSourceContact = (row: BaseQuery) => {
    if (row?.queryDetails?.__typename === 'NonAbapQuery') {
      return row.queryDetails?.sourceContactDisplayName || row.queryDetails?.sourceContact || '';
    }

    if (row?.queryDetails?.__typename === 'DitaQuery') {
      return row.queryDetails?.sourceContactDisplayName || row.queryDetails?.sourceContact || '';
    }

    return '';
  };

  const createRows = (baseQueries: BaseQuery[]) => {
    const getQueryDetailsValue = (queryDetails: QueryDetails) =>
      queryDetails.__typename === 'MarketingQuery'
        ? getMktQueryDetailsValue(queryDetails, MKT_FILENAME_PROP)
        : getXTMQueryDetailsValue(queryDetails, XTM_FILENAME_PROP);

    return baseQueries.map((row) => (
      <TableRow
        key={row.id}
        className={classes.tableRow}
        id={row.id}
        data-testid={`${row.id}-row`}
        interactive
      >
        <TableCell data-testid={`manage-queries-checkbox-cell-${row.id}`}>
          {userData?.user?.internal &&
          row.status !== Status.COMPLETED &&
          row.status !== Status.CLOSED ? (
            <CheckBox
              data-testid={`manage-queries-checkbox-${row.id}`}
              onChange={(event) => handleCheckboxClick(row, event)}
              checked={selectedIds.includes(row.id)}
              disabled={fetching}
              wrappingType="None"
            />
          ) : null}
        </TableCell>
        {searchColumnsVisibility.id && (
          <TableCell className={classes.queryCell}>
            {
              <Link to={`/queries/${row.id}`} target="_blank" rel="noopener noreferrer">
                {row.id}
              </Link>
            }
          </TableCell>
        )}
        {searchColumnsVisibility.deadline && (
          <TableCell className={classes.queryCell}>
            {getFormattedDateString(row.deadline)}
          </TableCell>
        )}
        {searchColumnsVisibility.status && (
          <TableCell className={classes.statusCell}>{t(row.status)}</TableCell>
        )}
        {searchColumnsVisibility.translation && (
          <TableCell className={classes.queryCell}>{t(row.translationType)}</TableCell>
        )}
        {searchColumnsVisibility.source && (
          <TableCell
            className={classes.queryCell}
            data-testid={`manage-queries-table-cell-source-${row.id}`}
          >
            {row.sourceText}
          </TableCell>
        )}
        {searchColumnsVisibility.issue && (
          <TableCell className={classes.queryCell}>{t(row.issueType)}</TableCell>
        )}
        {searchColumnsVisibility.language && (
          <TableCell className={classes.queryCell}>{row.targetLanguage}</TableCell>
        )}
        {searchColumnsVisibility.projectName && (
          <TableCell className={classes.queryCell}>{row.project.projectName}</TableCell>
        )}
        {searchColumnsVisibility.xtmProjectName && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails && getXTMQueryDetailsValue(row.queryDetails, XTM_PROJECTNAME_PROP)}
          </TableCell>
        )}
        {searchColumnsVisibility.filename && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails && getQueryDetailsValue(row.queryDetails)}
          </TableCell>
        )}
        {searchColumnsVisibility.loio && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails?.__typename === 'DitaQuery' ? row.queryDetails.loio : ''}
          </TableCell>
        )}
        {searchColumnsVisibility.repository && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails?.__typename === 'NonAbapQuery' ? row.queryDetails.repository : ''}
          </TableCell>
        )}
        {searchColumnsVisibility.collection && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails?.__typename === 'NonAbapQuery' ? row.queryDetails.collection : ''}
          </TableCell>
        )}
        {searchColumnsVisibility.stringId && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails?.__typename === 'NonAbapQuery' ? row.queryDetails.stringId : ''}
          </TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.applicationComponent && (
          <TableCell className={classes.queryCell}>{getApplicationComponent(row)}</TableCell>
        )}
        {allowQTImports && searchColumnsVisibility.queryTrackerId && (
          <TableCell className={classes.queryCell}>{row.qtId?.toString()}</TableCell>
        )}
        {searchColumnsVisibility.creator && (
          <TableCell
            data-testid={`manage-queries-list-table-cell-creator-${row.id}`}
            className={classes.queryCell}
          >
            {ResourceAccessLevel.OTHER_AGENCY === row.createdBy.accessLevel
              ? row.submitterGroupName
              : formatUserName({
                  userName: row.createdBy.displayName,
                  sapNumber: row.createdBy.sapNumber || undefined,
                  defaultValue: row.createdBy.displayName
                })}
          </TableCell>
        )}
        {searchColumnsVisibility.queryCreatedAt && (
          <TableCell
            data-testid={`manage-queries-list-table-cell-createdAt-${row.id}`}
            className={classes.queryCell}
          >
            {getFormattedDateString(row.createdAt, true)}
          </TableCell>
        )}
        {searchColumnsVisibility.submitterGroupName && (
          <TableCell className={classes.queryCell}>{row.submitterGroupName || ''}</TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.assignee && (
          <TableCell className={classes.queryCell}>
            {formatUserName({
              userName: row.assignedTo?.displayName,
              sapNumber: row.assignedTo?.sapNumber || undefined,
              defaultValue: row.assignee || undefined
            })}
          </TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.projectManager && (
          <TableCell className={classes.queryCell}>{getProjectManager(row.project)}</TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.qualityExpert && (
          <TableCell className={classes.queryCell}>{getQualityExpert(row.project)}</TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.qmContact && (
          <TableCell className={classes.queryCell}>{getQueryResponsible(row)}</TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.substitutePm && (
          <TableCell className={classes.queryCell}>
            {getSubstituteProjectManager(row.project)}
          </TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.account && (
          <TableCell className={classes.queryCell}>
            {row.queryDetails?.__typename === 'MarketingQuery' ? row.queryDetails.account : ''}
          </TableCell>
        )}
        {isInternalUser() && searchColumnsVisibility.sourceContact && (
          <TableCell className={classes.queryCell}>{getSourceContact(row)}</TableCell>
        )}
        <TableCell className={classes.queryCell}>
          <Icon
            name="navigation-right-arrow"
            className={classes.alignRight}
            onClick={() => {
              handleTableRowArrowClick(row.id);
            }}
            data-testid={`manage-queries-table-link-${row.id}`}
            mode="Interactive"
          />
        </TableCell>
      </TableRow>
    ));
  };

  const onClickCreate = () => {
    history.push('/create');
  };

  const batchAssign = () => {
    const hasNonNewStatus = selectedQueries.some((query) => query.status !== 'NEW');
    setBatchAssignNonNewQuery(hasNonNewStatus);
    if (!hasNonNewStatus) {
      setIsAssignDialogOpen(true);
    }
  };

  const renderTableTitle = () => {
    return (
      <Toolbar design={ToolbarDesign.Transparent}>
        <Title wrappingType="None" level="H2" size="H2">
          {t('manage-queries-list-table-title', { count: rows.length, total })}
        </Title>
        <ToolbarSpacer />
        <Button icon="add" data-help-id="manage-queries-button-create" onClick={onClickCreate}>
          {t('manage-queries-button-create')}
        </Button>
        {userData?.user?.internal && (
          <Button
            id="assign-button"
            key="assign-button"
            onClick={batchAssign}
            disabled={selectedIds.length === 0}
            className={classes.actionGroupItem}
            data-help-id="manage-queries-button-assign"
            data-testid="manage-queries-button-assign"
            design={ButtonDesign.Transparent}
          >
            {t('manage-queries-button-assign')}
          </Button>
        )}
        <ToolbarSeparator />
        <Button
          id="customize-button"
          key="customize-button"
          icon="action-settings"
          design={ButtonDesign.Transparent}
          tooltip={t('manage-queries-button-customize-tooltip')}
          className={classes.actionGroupItem}
          onClick={() => {
            setSearchColumnsDialogOpen(true);
          }}
          data-testid="manage-queries-button-customize"
          data-help-id="manage-queries-button-customize"
        />
        <OverflowMenu data-testid="table-menu" data-help-id="manage-queries-button-menu">
          <List separators={ListSeparators.Inner}>
            <ListItemStandard
              type={ListItemType.Inactive}
              key="download-results"
              tooltip={t('manage-queries-table-menu-download-results-tooltip', {
                maxDownloadableQueries: maxQueriesResult
              })}
              className={classes.popoverListItem}
              onClick={() => onDownloadResults()}
              data-help-id="manage-queries-table-menu-download-results"
              data-testid="manage-queries-table-menu-download-results"
            >
              <Icon className={classes.popoverListIcon} name="excel-attachment" />
              <Text className={classes.popoverListText} maxLines={1}>
                {t('manage-queries-table-menu-download-results')}
              </Text>
            </ListItemStandard>
            {userData?.user?.internal && allowQTImports && (
              <FeatureGuard requiredRoles={[QT_IMPORT_AZURE_ROLE_VALUE]}>
                <ListItemStandard
                  type={ListItemType.Inactive}
                  key="import-from-qt"
                  className={classes.popoverListItem}
                  onClick={() => setIsImportDialogOpen(true)}
                  data-testid="manage-queries-table-menu-import-from-qt"
                >
                  <Icon className={classes.popoverListIcon} name="upload" />
                  <Text className={classes.popoverListText} maxLines={1}>
                    {t('manage-queries-table-menu-import-from-qt')}
                  </Text>
                </ListItemStandard>
              </FeatureGuard>
            )}
          </List>
        </OverflowMenu>
      </Toolbar>
    );
  };

  const isFetchingData = () => {
    return fetching;
  };

  const getNoDataText = () => {
    if (isFetchingData()) {
      return t('manage-queries-loading-queries');
    }

    if (Object.keys(queryPageVariables.filters).length === 0) {
      return t('manage-queries-no-queries-found-without-filters');
    }
    return t('manage-queries-no-queries-found-with-filters');
  };

  const renderTable = () => {
    return (
      <>
        <Table
          ref={tableRef}
          loading={isFetchingData()}
          className={classes.queryTable}
          style={{ overflow: 'auto' }}
          noDataText={getNoDataText()}
          headerRow={renderTableColumns()}
          onRowClick={handleTableRowClick}
          features={
            <TableGrowing mode={TableGrowingMode.Scroll} onLoadMore={onLoadMore} slot="features" />
          }
        >
          {rows}
        </Table>
        <div className={classes.afterTableDiv}>{renderTableFooter()}</div>
      </>
    );
  };

  const renderTableColumns = () => {
    return (
      <TableHeaderRow sticky>
        <TableHeaderCell width="2rem">
          {userData?.user?.internal && (
            <CheckBox
              data-testid="manage-queries-select-all-checkbox"
              title={t('manage-queries-select-all-tooltip')}
              checked={allSelectableSelected || isSelectAllIndeterminate}
              indeterminate={isSelectAllIndeterminate}
              onChange={handleSelectAllChange}
              disabled={fetching || selectableQueries.length === 0}
              wrappingType="None"
            />
          )}
        </TableHeaderCell>
        {searchColumnsVisibility.id && (
          <TableHeaderCell width="5rem" data-testid="manage-queries-list-table-column-id">
            <span data-help-id="manage-queries-list-table-column-id">
              {t('manage-queries-list-table-column-id')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.deadline && (
          <TableHeaderCell width="9rem" data-testid="manage-queries-list-table-column-deadline">
            <span data-help-id="manage-queries-list-table-column-deadline">
              {t('manage-queries-list-table-column-deadline')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.status && (
          <TableHeaderCell width="9rem" data-testid="manage-queries-list-table-column-status">
            <span data-help-id="manage-queries-list-table-column-status">
              {t('manage-queries-list-table-column-status')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.translation && (
          <TableHeaderCell
            width="9rem"
            data-testid="manage-queries-list-table-column-translation-type"
          >
            <span data-help-id="manage-queries-list-table-column-translation-type">
              {t('manage-queries-list-table-column-translation-type')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.source && (
          <TableHeaderCell data-testid="manage-queries-list-table-column-source-text">
            <span data-help-id="manage-queries-list-table-column-source-text">
              {t('manage-queries-list-table-column-source-text')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.issue && (
          <TableHeaderCell data-testid="manage-queries-list-table-column-issue">
            <span data-help-id="manage-queries-list-table-column-issue">
              {t('manage-queries-list-table-column-issue')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.language && (
          <TableHeaderCell width="8rem" data-testid="manage-queries-list-table-column-language">
            <span data-help-id="manage-queries-list-table-column-language">
              {t('manage-queries-list-table-column-language')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.projectName && (
          <TableHeaderCell data-testid="manage-queries-list-table-column-project-name">
            <span data-help-id="manage-queries-list-table-column-project-name">
              {t('manage-queries-list-table-column-project-name')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.xtmProjectName && (
          <TableHeaderCell data-testid="manage-queries-list-table-column-xtm-project-name">
            <span data-help-id="manage-queries-list-table-column-xtm-project-name">
              {t('manage-queries-list-table-column-xtm-project-name')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.filename && (
          <TableHeaderCell data-testid="manage-queries-list-table-column-file-name">
            <span data-help-id="manage-queries-list-table-column-file-name">
              {t('manage-queries-list-table-column-file-name')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.loio && (
          <TableHeaderCell width="5rem" data-testid="manage-queries-list-table-column-loio">
            <span data-help-id="manage-queries-list-table-column-loio">
              {t('manage-queries-list-table-column-loio')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.repository && (
          <TableHeaderCell width="7rem" data-testid="manage-queries-list-table-column-repository">
            <span data-help-id="manage-queries-list-table-column-repository">
              {t('manage-queries-list-table-column-repository')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.collection && (
          <TableHeaderCell width="7rem" data-testid="manage-queries-list-table-column-collection">
            <span data-help-id="manage-queries-list-table-column-collection">
              {t('manage-queries-list-table-column-collection')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.stringId && (
          <TableHeaderCell width="6rem" data-testid="manage-queries-list-table-column-string-id">
            <span data-help-id="manage-queries-list-table-column-string-id">
              {t('manage-queries-list-table-column-string-id')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.applicationComponent && (
          <TableHeaderCell
            width="11rem"
            data-testid="manage-queries-list-table-column-application-component"
          >
            <span data-help-id="manage-queries-list-table-column-application-component">
              {t('manage-queries-list-table-column-application-component')}
            </span>
          </TableHeaderCell>
        )}
        {allowQTImports && searchColumnsVisibility.queryTrackerId && (
          <TableHeaderCell
            width="9rem"
            data-testid="manage-queries-list-table-column-query-tracker-id"
          >
            <span data-help-id="manage-queries-list-table-column-query-tracker-id">
              {t('manage-queries-list-table-column-query-tracker-id')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.creator && (
          <TableHeaderCell width="7rem" data-testid="manage-queries-list-table-column-creator">
            <span data-help-id="manage-queries-list-table-column-creator">
              {t('manage-queries-list-table-column-creator')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.queryCreatedAt && (
          <TableHeaderCell width="7rem" data-testid="manage-queries-list-table-column-createdAt">
            <span data-help-id="manage-queries-list-table-column-createdAt">
              {t('manage-queries-list-table-column-createdAt')}
            </span>
          </TableHeaderCell>
        )}
        {searchColumnsVisibility.submitterGroupName && (
          <TableHeaderCell
            width="10rem"
            data-testid="manage-queries-list-table-column-submitter-group-name"
          >
            <span data-help-id="manage-queries-list-table-column-submitter-group-name">
              {t('manage-queries-list-table-column-submitter-group-name')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.assignee && (
          <TableHeaderCell width="7rem" data-testid="manage-queries-list-table-column-assignee">
            <span data-help-id="manage-queries-list-table-column-assignee">
              {t('manage-queries-list-table-column-assignee')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.projectManager && (
          <TableHeaderCell
            width="9rem"
            data-testid="manage-queries-list-table-column-project-manager"
          >
            <span data-help-id="manage-queries-list-table-column-project-manager">
              {t('manage-queries-list-table-column-project-manager')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.qualityExpert && (
          <TableHeaderCell
            width="9rem"
            data-testid="manage-queries-list-table-column-quality-engineer"
          >
            <span data-help-id="manage-queries-list-table-column-quality-engineer">
              {t('manage-queries-list-table-column-quality-engineer')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.qmContact && (
          <TableHeaderCell width="13rem" data-testid="manage-queries-list-table-column-qm-contact">
            <span data-help-id="manage-queries-list-table-column-qm-contact">
              {t('manage-queries-list-table-column-qm-contact')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.substitutePm && (
          <TableHeaderCell
            width="13rem"
            data-testid="manage-queries-list-table-column-substitute-pm"
          >
            <span data-help-id="manage-queries-list-table-column-substitute-pm">
              {t('manage-queries-list-table-column-substitute-pm')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.account && (
          <TableHeaderCell width="6rem" data-testid="manage-queries-list-table-column-account">
            <span data-help-id="manage-queries-list-table-column-account">
              {t('manage-queries-list-table-column-account')}
            </span>
          </TableHeaderCell>
        )}
        {isInternalUser() && searchColumnsVisibility.sourceContact && (
          <TableHeaderCell
            width="8rem"
            data-testid="manage-queries-list-table-column-source-contact"
          >
            <span data-help-id="manage-queries-list-table-column-source-contact">
              {t('manage-queries-list-table-column-source-contact')}
            </span>
          </TableHeaderCell>
        )}
        <TableHeaderCell width="3rem" />
      </TableHeaderRow>
    );
  };

  return (
    <>
      <div className={classes.outerWrapper}>
        <div ref={headerRef} className={classes.filterWrapper}>
          <div className={classes.filterDiv} data-help-id="manage-queries-filters">
            <Title wrappingType="None" level="H2" size="H2">
              {t('manage-queries-title')}
            </Title>
            <Filters
              onFilter={handleFilterChange}
              onSearchQueriesFetchChange={handleSearchQueriesFetchChange}
              internal={userData?.user?.internal ?? false}
            />
          </div>
          <div className={classes.tableHeaderDiv}>{renderTableTitle()}</div>
        </div>
        <div
          className={classes.tableDiv}
          data-help-id="manage-queries-table"
          id="manage-queries-table"
        >
          {renderTable()}
        </div>
      </div>
      <SearchColumnsDialog
        open={searchColumnsDialogOpen}
        isInternalUser={isInternalUser()}
        setSearchColumnsDialogOpen={setSearchColumnsDialogOpen}
        searchColumnsVisibility={searchColumnsVisibility}
        setSearchColumnsVisibility={onConfirmColumnVisibility}
      />
      <BatchAssignMessageBox
        batchAssignNonNewQuery={batchAssignNonNewQuery}
        setBatchAssignNonNewQuery={setBatchAssignNonNewQuery}
        setIsAssignDialogOpen={setIsAssignDialogOpen}
      />
      <AssignDialog
        isDialogOpen={isAssignDialogOpen}
        queries={selectedQueries}
        setIsDialogOpen={setIsAssignDialogOpen}
        setResultMessage={onResultUserMessage}
      />
      {allowQTImports && (
        <FeatureGuard requiredRoles={[QT_IMPORT_AZURE_ROLE_VALUE]}>
          <ImportDialog isDialogOpen={isImportDialogOpen} setIsDialogOpen={setIsImportDialogOpen} />
        </FeatureGuard>
      )}
      <UserMessage
        isVisible={isVisibleUserMessage}
        resultMessage={resultUserMessage}
        onResetUserMessage={onResetUserMessage}
      />
      <Toast
        data-testid="manage-queries-toast"
        ref={toastRef}
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        duration={TOAST_DURATION}
      >
        {toastMessage}
      </Toast>
    </>
  );
};
